// ── Thermal Throttle Detection ──
// Monitors frame-time variance to detect thermal throttling on mobile devices.
// No JS API exists for temperature, so we use frame-time heuristics.

import type { GraphicsQuality } from '../core/settings';

/** Quality tier downgrade map: current → one tier lower. */
const QUALITY_DOWNGRADE: Partial<Record<GraphicsQuality, GraphicsQuality>> = {
  'ultra': 'high',
  'high': 'medium',
  'medium': 'low',
  'mobile-high': 'mobile-medium',
  'mobile-medium': 'mobile-low',
  // 'low' and 'mobile-low' have no further downgrade
};

/** Target frame time budget in ms (60fps). */
const TARGET_FRAME_TIME = 16.67;

/**
 * How many frame time samples to keep.
 * At 60fps, 1800 samples = 30 seconds.
 */
const WINDOW_SIZE = 1800;

/**
 * Minimum number of samples before throttle detection kicks in.
 * Need enough data to split into two meaningful halves.
 * At 60fps, 600 samples ≈ 10 seconds.
 */
const MIN_SAMPLES = 600;

/** Percentage increase in frame time to consider as throttling. */
const THROTTLE_THRESHOLD = 0.30;

/** How many frames under budget before considering DPR increase. */
const DPR_INCREASE_WINDOW = 1800; // ~30s at 60fps

/** Under-budget threshold: if average is under TARGET * (1 - threshold), consider increase. */
const UNDER_BUDGET_THRESHOLD = 0.30;

/** DPR increment step. */
const DPR_STEP = 0.25;

/** Minimum DPR floor. */
const DPR_FLOOR = 0.75;

export class ThermalThrottleDetector {
  private _frameTimes: number[] = [];
  private _throttling = false;
  private _downgradeEmitted = false;
  private _onDowngrade: (newQuality: string) => void;
  private _currentQuality: GraphicsQuality;
  private _currentDpr: number;
  private _maxDpr: number;
  private _disposed = false;

  // Tracking for DPR adaptive scaling
  private _underBudgetCount = 0;

  /**
   * @param onDowngrade Called when thermal throttling triggers a quality downgrade.
   * @param currentQuality Starting quality tier (default: 'mobile-high').
   * @param initialDpr Starting device pixel ratio (default: 1).
   * @param maxDpr Maximum DPR ceiling (default: device DPR or 2).
   */
  constructor(
    onDowngrade: (newQuality: string) => void,
    currentQuality: GraphicsQuality = 'mobile-high',
    initialDpr = 1,
    maxDpr = 2,
  ) {
    this._onDowngrade = onDowngrade;
    this._currentQuality = currentQuality;
    this._currentDpr = Math.max(DPR_FLOOR, initialDpr);
    this._maxDpr = maxDpr;
  }

  /**
   * Record a frame time in milliseconds. Call every frame.
   * Performs throttle detection and adaptive DPR scaling.
   */
  recordFrameTime(dt: number): void {
    if (this._disposed) return;

    this._frameTimes.push(dt);

    // Amortized O(1) trim: keep at most 2×WINDOW_SIZE entries and only
    // reslice to the most recent WINDOW_SIZE when the buffer doubles.
    // A per-frame splice would copy up to WINDOW_SIZE elements on every call.
    if (this._frameTimes.length >= WINDOW_SIZE * 2) {
      this._frameTimes = this._frameTimes.slice(-WINDOW_SIZE);
    }

    // Check throttling once we have enough samples to compare halves.
    // Counts only the effective sliding window, not the amortization overflow.
    const effectiveLen = Math.min(this._frameTimes.length, WINDOW_SIZE);
    if (effectiveLen >= MIN_SAMPLES) {
      this._checkThrottling();
    }

    // Adaptive DPR scaling — check if consistently under budget
    this._checkUnderBudget(dt);
  }

  /** Get the average FPS over the current sample window. */
  getAverageFps(): number {
    const offset = Math.max(0, this._frameTimes.length - WINDOW_SIZE);
    const len = this._frameTimes.length - offset;
    if (len === 0) return 0;
    let sum = 0;
    for (let i = offset; i < this._frameTimes.length; i++) sum += this._frameTimes[i];
    const avgMs = sum / len;
    if (avgMs <= 0) return 0;
    return 1000 / avgMs;
  }

  /** Whether the detector currently believes the device is thermal throttling. */
  isThrottling(): boolean {
    return this._throttling;
  }

  /** Get the current adaptive DPR value. */
  getCurrentDpr(): number {
    return this._currentDpr;
  }

  /** Reset all state — clears frame time history and throttle flags. */
  reset(): void {
    this._frameTimes = [];
    this._throttling = false;
    this._downgradeEmitted = false;
    this._underBudgetCount = 0;
  }

  /** Dispose the detector. */
  dispose(): void {
    this._disposed = true;
    this._frameTimes = [];
  }

  /**
   * Compare the average frame time of the first half of available samples
   * to the second half. If the second half is >30% worse, flag throttling.
   */
  private _checkThrottling(): void {
    // Only look at the most recent WINDOW_SIZE samples so amortized growth
    // past WINDOW_SIZE doesn't drift the detection window.
    const offset = Math.max(0, this._frameTimes.length - WINDOW_SIZE);
    const len = this._frameTimes.length - offset;
    const mid = Math.floor(len / 2);
    const firstLen = mid;
    const secondLen = len - mid;

    if (firstLen === 0 || secondLen === 0) return;

    let sumFirst = 0;
    for (let i = offset; i < offset + mid; i++) sumFirst += this._frameTimes[i];
    let sumSecond = 0;
    for (let i = offset + mid; i < this._frameTimes.length; i++) sumSecond += this._frameTimes[i];

    const avgFirst = sumFirst / firstLen;
    const avgSecond = sumSecond / secondLen;

    if (avgFirst <= 0) return;

    const increase = (avgSecond - avgFirst) / avgFirst;

    if (increase > THROTTLE_THRESHOLD) {
      this._throttling = true;
      if (!this._downgradeEmitted) {
        this._emitDowngrade();
      }
    }
  }

  /** Emit a quality downgrade if a lower tier exists. */
  private _emitDowngrade(): void {
    const nextTier = QUALITY_DOWNGRADE[this._currentQuality];
    if (nextTier) {
      this._downgradeEmitted = true;
      this._currentQuality = nextTier;
      this._onDowngrade(nextTier);
    }
    // If already at lowest tier, don't emit — nothing to do
  }

  /** Track frames under budget and increase DPR if consistently under. */
  private _checkUnderBudget(dt: number): void {
    const budgetThreshold = TARGET_FRAME_TIME * (1 - UNDER_BUDGET_THRESHOLD);

    if (dt < budgetThreshold) {
      this._underBudgetCount++;
    } else {
      // Reset counter if any frame exceeds the threshold
      this._underBudgetCount = 0;
    }

    if (this._underBudgetCount >= DPR_INCREASE_WINDOW) {
      // Increase DPR by one step, capped at max
      const newDpr = Math.min(this._currentDpr + DPR_STEP, this._maxDpr);
      if (newDpr !== this._currentDpr) {
        this._currentDpr = newDpr;
      }
      // Reset counter to avoid continuous increases
      this._underBudgetCount = 0;
    }
  }
}
